import type { SubscriptionResource, Plan } from '@/types/subscription';

/**
 * Check if subscription is currently on trial — reads the API-computed
 * `is_on_trial` flag.
 */
export function isOnTrial(subscription: SubscriptionResource | null): boolean {
  return subscription?.attributes?.is_on_trial === true;
}

/**
 * Get days remaining in trial
 */
export function getTrialDaysRemaining(
  subscription: SubscriptionResource | null
): number {
  if (!subscription?.attributes?.trial_ends_at) return 0;

  const trialEndDate = new Date(subscription.attributes.trial_ends_at);
  const now = new Date();
  const diff = trialEndDate.getTime() - now.getTime();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

  return Math.max(0, days);
}

/**
 * Get hours remaining in trial
 */
export function getTrialHoursRemaining(
  subscription: SubscriptionResource | null
): number {
  if (!subscription?.attributes?.trial_ends_at) return 0;

  const trialEndDate = new Date(subscription.attributes.trial_ends_at);
  const now = new Date();
  const diff = trialEndDate.getTime() - now.getTime();
  const hours = Math.ceil(diff / (1000 * 60 * 60));

  return Math.max(0, hours);
}

/**
 * Get trial end date
 */
export function getTrialEndDate(
  subscription: SubscriptionResource | null
): Date | null {
  if (!subscription?.attributes?.trial_ends_at) return null;

  return new Date(subscription.attributes.trial_ends_at);
}

/**
 * Check if subscription is in grace period — reads the API-computed
 * `is_in_grace_period` flag.
 */
export function isInGracePeriod(
  subscription: SubscriptionResource | null
): boolean {
  return subscription?.attributes?.is_in_grace_period === true;
}

/**
 * Check if subscription is in billing retry period (Apple/Google)
 */
export function isInBillingRetry(
  subscription: SubscriptionResource | null
): boolean {
  if (!subscription?.attributes) return false;
  return subscription.attributes.is_in_billing_retry === true;
}

/**
 * Check if subscription grants access — reads the API-computed `grants_access` flag.
 * Works for any payment provider (Apple, Google, Stripe).
 */
export function isSubscriptionGrantingAccess(
  subscription: SubscriptionResource | null
): boolean {
  if (!subscription?.attributes) return false;
  return subscription.attributes.grants_access === true;
}

/**
 * Get grace period days remaining
 */
export function getGracePeriodDaysRemaining(
  subscription: SubscriptionResource | null
): number {
  if (!subscription?.attributes?.grace_period_ends_at) return 0;

  const graceEndDate = new Date(subscription.attributes.grace_period_ends_at);
  const now = new Date();
  const diff = graceEndDate.getTime() - now.getTime();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

  return Math.max(0, days);
}

/**
 * Check if plan is a trial plan
 */
export function isTrialPlan(plan: Plan | null): boolean {
  if (!plan) return false;
  return plan.is_trial === true;
}

/**
 * Get target plan ID for trial conversion
 */
export function getTrialTargetPlanId(
  plan: Plan | null
): string | number | null {
  if (!plan?.trial_target_plan_id) return null;
  return plan.trial_target_plan_id;
}
